import { memo } from 'react';
import styled from 'styled-components';
import { useIsFetching } from '@tanstack/react-query';
import { ColorNeutralWhite } from 'autogen/design-tokens/tokens';
import LoadingContainer from 'components/checkout/components/BasketOverview/components/LoadingContainer';
import DefaultContentRenderer from 'components/generic/Drawer/components/DefaultContentRenderer';
import DefaultHeaderRenderer from 'components/generic/Drawer/components/DefaultHeaderRenderer';
import DrawerView from 'components/generic/Drawer/DrawerView';
import type { BaseDrawerViewProps } from 'components/generic/Drawer/shared/types';
import Menu from 'components/generic/menu';
import type { MenuHeaderProps } from 'components/sideMenu/components/Menu/components/MenuHeader';
import { customScrollbar } from 'modules/helpers/mixins';
import SearchInput from '../Search/SearchInput';
import FilterMenuFooter from './components/FilterMenuFooter';

/* #region styles */
const StyledDrawerView = styled(DrawerView)`
    flex-direction: column;
    background: ${ColorNeutralWhite};
`;

const StyledLoadingContainer = styled(LoadingContainer)`
    ${customScrollbar}
    height: 100%;
    overflow: auto;
`;

/* #endregion */

type FilterMenuProps = BaseDrawerViewProps &
    MenuHeaderProps & {
        withDividers?: boolean;
        withFooter?: boolean;
        withHeader?: boolean;
        withSearch?: boolean;
        footerRenderer?: () => JSX.Element;
    };

const FilterMenuDrawer = ({
    children,
    headline,
    backButtonCallback,
    closeButtonCallback,
    withFooter = true,
    withHeader = true,
    withSearch = false,
    withDividers = false,
    footerRenderer = () => <FilterMenuFooter />,
    animate = false,
    ...restProps
}: FilterMenuProps) => {
    const isLoading = useIsFetching({ queryKey: ['filterMenuData'] });

    return (
        <StyledDrawerView
            position="right"
            fullscreen
            headerRenderer={
                withHeader
                    ? (headerProps) => <DefaultHeaderRenderer.WithHeading {...headerProps} heading={headline} />
                    : undefined
            }
            contentRenderer={(contentProps) => <DefaultContentRenderer.NoPadding {...contentProps} />}
            footerRenderer={withFooter ? () => footerRenderer() : undefined}
            animate={animate}
            {...restProps}
        >
            <StyledLoadingContainer canUserInteract={false} isLoading={!!isLoading}>
                <Menu size="lg" shouldFocus>
                    {withSearch && <SearchInput />}
                    <Menu.Section listProps={{ divider: withDividers }}>{children}</Menu.Section>
                </Menu>
            </StyledLoadingContainer>
        </StyledDrawerView>
    );
};

export default memo(FilterMenuDrawer);
